import type { AppearanceThemeId, ThemeRgb } from './themes';

/** Render-only hash: independent of simulation RNG and traversal/load order. */
export function appearanceNoise(seed: number, theme: AppearanceThemeId, objectId: string): number {
  let h = (seed >>> 0) ^ 0x811c9dc5;
  for (const c of `${theme}:${objectId}`) h = Math.imul(h ^ c.charCodeAt(0), 0x01000193);
  h = Math.imul(h ^ (h >>> 16), 0x7feb352d);
  h = Math.imul(h ^ (h >>> 15), 0x846ca68b);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** Eight coordinated finishes keep batching/material counts bounded. Functional
 * colors remain authored; skin varies only in luminance, never arbitrary hue. */
export function variedColor(rgb: ThemeRgb, seed: number, theme: AppearanceThemeId, objectId: string, role = ''): ThemeRgb {
  if (role === 'functional') return rgb;
  const bucket = Math.floor(appearanceNoise(seed, theme, objectId) * 8);
  const step = (bucket - 3.5) / 3.5;
  const neutral = theme === 'graybox';
  const skin = role === 'skin';
  const surface = /road|ground|infrastructure/.test(role);
  const strength = neutral ? .08 : skin ? .13 : surface ? .07 : .16;
  const luminance = (rgb[0] + rgb[1] + rgb[2]) / 3;
  const warmth = skin || neutral || surface ? 0 : Math.sin(bucket * 2.39996) * .035;
  return rgb.map((v, i) => {
    const base = neutral ? luminance : luminance + (v - luminance) * (1 + step * .14);
    return Math.max(.025, Math.min(1, base * (1 + step * strength) + (i === 0 ? warmth : i === 2 ? -warmth : 0)));
  }) as unknown as ThemeRgb;
}

export interface ActorMotionVariation { phase: number; cadence: number; amplitude: number; idleRate: number; }
export function actorMotionVariation(seed: number, theme: AppearanceThemeId, actorId: string): ActorMotionVariation {
  return {
    phase: appearanceNoise(seed, theme, `${actorId}:phase`) * Math.PI * 2,
    cadence: .88 + appearanceNoise(seed, theme, `${actorId}:cadence`) * .24,
    amplitude: .72 + appearanceNoise(seed, theme, `${actorId}:amplitude`) * .28,
    idleRate: .65 + appearanceNoise(seed, theme, `${actorId}:idle`) * .4,
  };
}

/** Small, occasional conversation gestures; never used for research cue timing. */
export function idleGesture(t: number, motion: ActorMotionVariation): number {
  return Math.pow(Math.max(0, Math.sin(t * motion.idleRate + motion.phase)), 6) * .22 * motion.amplitude;
}
